"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import YourBarrioLogo from "@/components/brand/YourBarrioLogo";
import PwaInstallButton from "@/components/pwa/PwaInstallButton";
import SupportAuthLink from "@/components/auth/SupportAuthLink";

export default function Footer({ className = "" }) {
  const pathname = usePathname();
  const isAdminRoute = pathname?.startsWith("/admin");
  const footerLinkClass =
    "inline-flex w-fit text-sm text-slate-300/88 transition-colors duration-200 ease-out hover:text-[#cbb6ff] focus-visible:text-[#cbb6ff] focus-visible:outline-none sm:text-[0.95rem]";
  const footerHeadingClass =
    "text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-slate-300/78";

  return (
    <footer
      className={`theme-lock relative w-full overflow-hidden border-t border-white/8 bg-[#171920] py-10 text-white sm:py-14 ${
        className || (isAdminRoute ? "mt-0" : "mt-20")
      }`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8b5cf6]/28 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white/[0.035] to-transparent" />
      <div className="w-full px-5 sm:px-6 md:px-8 lg:px-12">
        <div
          className="mx-auto grid max-w-[1180px] grid-cols-1 gap-8 text-slate-300 sm:grid-cols-2 sm:gap-10 md:grid-cols-[minmax(0,1.2fr)_0.48fr_0.72fr_0.62fr] md:gap-8 lg:gap-11"
        >
          {/* BRAND COLUMN */}
          <div className="max-w-[19rem]">
            <Link
              href="/"
              className="group inline-flex cursor-pointer select-none items-center gap-3 text-white transition-colors duration-200 ease-out hover:text-white"
            >
              <YourBarrioLogo
                variant="navbar-dark"
                alt="YourBarrio logo"
                sizes="(min-width: 768px) 144px, 137px"
                className="h-[38px] w-auto select-none md:h-10"
                draggable={false}
              />
            </Link>

            <p className="mt-4 max-w-[15.5rem] text-sm leading-6 text-slate-400 sm:mt-5 sm:text-[0.95rem]">
              Discover and shop local businesses around you.
            </p>

            <div className="mt-4">
              <Link
                href="/business"
                prefetch={false}
                className="inline-flex w-fit text-[0.95rem] font-medium text-[#a78bfa] transition-all duration-200 ease-out hover:text-[#c4b5fd] hover:underline hover:underline-offset-4 focus-visible:text-[#c4b5fd] focus-visible:underline focus-visible:underline-offset-4 focus-visible:outline-none"
              >
                For local businesses →
              </Link>
            </div>

            <PwaInstallButton />
          </div>

          {/* COMPANY COLUMN */}
          <div className="md:pt-2">
            <h4 className={footerHeadingClass}>Company</h4>
            <ul className="mt-4 space-y-3 sm:mt-5 sm:space-y-3.5">
              <li>
                <Link href="/about" className={footerLinkClass}>
                  About
                </Link>
              </li>
              <li>
                <Link href="/business/faq" className={footerLinkClass}>
                  Business FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* LEGAL COLUMN */}
          <div className="md:pt-2">
            <h4 className={footerHeadingClass}>Legal</h4>
            <ul className="mt-4 space-y-3 sm:mt-5 sm:space-y-3.5">
              <li>
                <Link href="/terms" className={footerLinkClass}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link href="/privacy" className={footerLinkClass}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/refund-policy" className={footerLinkClass}>
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link href="/business-terms" className={footerLinkClass}>
                  Business Terms
                </Link>
              </li>
              <li>
                <Link href="/prohibited-categories" className={footerLinkClass}>
                  Category Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* CONTACT COLUMN */}
          <div className="md:pt-2">
            <h4 className={footerHeadingClass}>Contact</h4>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-400 sm:mt-5 sm:space-y-3.5 sm:text-[0.95rem]">
              <li>
                <SupportAuthLink href="/support" className={footerLinkClass}>
                  YourBarrio Support
                </SupportAuthLink>
              </li>
              <li>
                <a
                  href="mailto:support@yourbarrio.com"
                  className="inline-flex w-fit transition-colors duration-200 ease-out hover:text-[#cbb6ff] focus-visible:text-[#cbb6ff] focus-visible:outline-none"
                >
                  support@yourbarrio.com
                </a>
              </li>
              <li className="text-slate-400/92">
                Long Beach, CA
              </li>
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-[1180px] border-t border-white/8 pt-6 sm:mt-16 sm:pt-8">
          <div className="flex flex-col gap-3 text-xs tracking-[0.08em] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-left">
              © {new Date().getFullYear()} YourBarrio. All rights reserved.
            </div>
            <div className="hidden text-[0.7rem] uppercase tracking-[0.24em] text-slate-600 sm:block">
              Local discovery, refined
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
